import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { cx } from '@/lib/cx';
import { formatDateOnly } from '@/lib/format';
import { KANBAN_DATE_LABEL, dateTone } from '@/lib/kanban-utils';
import { QUOTE_SIGNED_LABEL, quoteSignedLabel } from '@/lib/deal-quote-signed';
import type {
  CompanyStageStatusRef,
  KanbanCard as KanbanCardModel,
  KanbanDateField,
} from '@/lib/types';
import { Avatar, Badge, Button } from '@/components/ui';
import { Money } from '@/components/ui/Money';

export interface KanbanCardProps {
  card: KanbanCardModel;
  /**
   * Which date the board is showing, or NULL when it shows none.
   *
   * Null is the board's own answer, not a missing prop: companies carry only
   * created/updated and neither is a pipeline date, so `meta.dateField` is
   * null and every `card.date` with it. The card then renders no date, no
   * label and no slot — see the date block below.
   */
  dateField: KanbanDateField | null;
  /** Detail-page route for this record. */
  href: string;
  /** Active, non-null columns this card may be moved into — EXCLUDING its own. */
  targets: { id: number; name: string }[];
  /** False for a viewer, who gets a 403 from the move endpoint. */
  canMove: boolean;
  /** A move is in flight for this card. */
  moving: boolean;
  onMove: (toColumnId: number) => void;
  /** Whether to render the reference line at all — always null on deals. */
  showReference: boolean;
  /**
   * The page's own per-card action, rendered in the card's top-right corner.
   *
   * Supplied by the page and opaque to this card, exactly like the board's
   * `create` action: the card does not know what document the action raises,
   * who may raise one, or what the menu contains. **Absence is the gate** —
   * omit it and nothing renders, so a page that may not create passes nothing
   * and there is no `canCreateDocument` prop to get wrong at one call site.
   *
   * Rendered above the stretched link and outside the drag source; see the
   * block itself for how, and why `draggable={false}` alone would not do it.
   */
  cardAction?: (card: KanbanCardModel) => ReactNode;
  /**
   * How many icon-sized controls `cardAction` renders (default 1), so the
   * title keeps clear of all of them. Added 2026-10-25 when the quotes board
   * put Preview PDF beside Convert.
   */
  cardActionCount?: number;
  /**
   * The name of the column this card sits in. Optional: used only to name the
   * stage in the companies card's status label ("Prospect status: …"); when
   * omitted the label falls back to "Stage status: …".
   */
  columnName?: string;
}

/**
 * The companies board card. `stageStatus` is not on the shared
 * `KanbanCard` type in lib/types.ts: the server adds it ONLY when the board's
 * table is `d_companies` (kanban.service.ts `toCard`), so it is absent on the
 * deals, quotes, invoices and deliverables boards, and null when the company
 * has no status. Narrowed locally rather than widening the shared type.
 */
type CompaniesBoardCard = KanbanCardModel & {
  stageStatus?: CompanyStageStatusRef | null;
};
const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** Arrow-right-left: the card changes column, it does not "go" anywhere. */
function MoveIcon() {
  return (
    <svg {...stroke} className="h-4 w-4 shrink-0">
      <path d="M4 8h13" />
      <path d="m14 5 3 3-3 3" />
      <path d="M20 16H7" />
      <path d="m10 13-3 3 3 3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg {...stroke} className="h-4 w-4 shrink-0">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...stroke} className="h-4 w-4 shrink-0">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg {...stroke} className="h-4 w-4 shrink-0">
      <path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

/**
 * The organisation the record belongs to — a school, in this CRM's vocabulary.
 *
 * A columned building rather than a generic box: `company` sits directly under
 * a title that frequently *also* names an institution ("University of
 * Johannesburg - Lead & Application Engagement"), and the glyph is what says
 * "this line is the account" without a printed "Company:" label on every card.
 */
function CompanyIcon() {
  return (
    <svg {...stroke} className="mt-px h-4 w-4 shrink-0 text-fg-subtle">
      <path d="M3 21h18" />
      <path d="M5 21V8l7-4 7 4v13" />
      <path d="M10 21v-5h4v5" />
      <path d="M9.5 11h.01M14.5 11h.01" />
    </svg>
  );
}

/** Stand-in for an owner who has a record but no usable name. */
function PersonIcon() {
  return (
    <svg {...stroke} className="h-4 w-4 shrink-0 text-fg-subtle">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

const MENU_MAX_HEIGHT = 260;

/*
 * Tone is never the only signal: each state carries a glyph and a word as well
 * as a colour, so it survives greyscale, low vision, and a phone in sunlight.
 *
 * The two urgent states are also promoted OUT of the quiet meta line into a
 * badge, which is the only bordered, filled thing on an otherwise flat card —
 * in a column of cards, the one that has slipped has to be findable without
 * reading. `normal` stays deliberately plain: if every date drew attention,
 * none of them would. Plain is not faint, though — the date is one of the three
 * things this board is read for, so even the calm state is `fg-secondary` at
 * 12px with a glyph, not grey small print.
 */
const DATE_TONE = {
  overdue: { badge: 'danger', word: 'Overdue', Icon: AlertIcon },
  'due-soon': { badge: 'warning', word: 'Due soon', Icon: ClockIcon },
  normal: { badge: null, word: null, Icon: CalendarIcon },
} as const;

/**
 * One record on a kanban board.
 *
 * Designed for the EMPTY card first, because in production that IS the card:
 * `company`, `contact` and `owner` are null on essentially every deal, quote
 * and invoice, and `title` is frequently null. Every block is conditional and
 * nothing reserves space for an absent field, so a card with only an id still
 * reads as a card, and a populated card is the richer variant, not the norm.
 *
 * Three tiers, and only three:
 *   1. IDENTITY — the title, then `company` beneath it with a building glyph;
 *      company is the "school name" this board is read for.
 *   2. FACTS — date, then amount, then reference, behind a hairline rule. The
 *      date escalates to a badge when overdue or due soon.
 *   3. PEOPLE — contact and owner, folded onto one wrapping line.
 *
 * All three can be absent — a companies-board card has no date
 * (`dateField: null`), no money and no `company`, leaving just the title and
 * owner, which is the whole record.
 *
 * There is no product on a card: the API returns none, and deal titles like
 * "Institution - Engagement" are ONE string — splitting on the hyphen to fake
 * a product field breaks on every name that itself contains one.
 *
 * The whole surface is one link, laid OVER the content rather than wrapping
 * it: a `<button>` nested inside an `<a>` is invalid HTML. The Move control
 * sits above the overlay and stops its own clicks, so no path through it
 * navigates.
 *
 * Drag-and-drop belongs to the board, which sets `draggable` and the handlers
 * on the wrapping `<li>`. This card adds none — only `draggable={false}` on
 * the link and the Move button, so a drag started on either becomes the
 * board's card drag rather than a native link drag.
 */
export function KanbanCard({
  card,
  dateField,
  href,
  targets,
  canMove,
  moving,
  onMove,
  showReference,
  cardAction,
  cardActionCount = 1,
  columnName,
}: KanbanCardProps): ReactElement {
  const titleId = useId();
  const menuId = useId();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState({ right: 0, top: 0, bottom: 0, flip: false });

  const linkRef = useRef<HTMLAnchorElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const hasTargets = targets.length > 0;
  const menuDisabled = moving || !hasTargets;
  /*
   * Derived rather than reconciled in an effect: a move in flight disables the
   * trigger, and an open menu hanging over a dead button strands focus on
   * nothing. Computing it here closes the menu in the same render that
   * disables the button, with no extra pass and no cascading setState.
   */
  const menuOpen = open && !menuDisabled;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const below = window.innerHeight - box.bottom;
    setRect({
      // Right-anchored: the trigger sits in the card's top-right corner, and a
      // left-anchored menu would hang off the board on a 320px screen.
      right: Math.max(window.innerWidth - box.right, 8),
      top: box.top,
      bottom: box.bottom,
      flip: below < MENU_MAX_HEIGHT + 16 && box.top > below,
    });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) return;
    place();
  }, [menuOpen, place]);

  // A kanban column scrolls vertically inside a board that scrolls
  // horizontally, so a fixed menu has to follow its trigger or it detaches and
  // floats over an unrelated column.
  useEffect(() => {
    if (!menuOpen) return;
    const reposition = () => place();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [menuOpen, place]);

  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  // Focus follows the active item rather than `aria-activedescendant`: these
  // are real buttons, so real focus is both simpler and better supported.
  useEffect(() => {
    if (!menuOpen) return;
    itemRefs.current[activeIndex]?.focus();
  }, [menuOpen, activeIndex]);

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function commit(columnId: number) {
    setOpen(false);
    // Not back to the trigger: it is about to be disabled by `moving`, and
    // focus on a disabled control falls to <body>, taking the reading position
    // with it. The card's own link stays focusable and names the record.
    linkRef.current?.focus();
    onMove(columnId);
  }

  function onTriggerClick(event: ReactMouseEvent) {
    // The card is a link. The menu must never navigate.
    event.preventDefault();
    event.stopPropagation();
    if (menuOpen) {
      close(false);
      return;
    }
    if (menuDisabled) return;
    setActiveIndex(0);
    setOpen(true);
  }

  function onTriggerKeyDown(event: ReactKeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (menuDisabled) return;
      setActiveIndex(event.key === 'ArrowUp' ? targets.length - 1 : 0);
      setOpen(true);
      return;
    }
    if (event.key === 'Escape' && menuOpen) {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
  }

  function onMenuKeyDown(event: ReactKeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    // Tabbing out dismisses, but the browser still gets to move focus onward.
    if (event.key === 'Tab') {
      close(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    setActiveIndex((current) => {
      if (event.key === 'Home') return 0;
      if (event.key === 'End') return targets.length - 1;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      return (current + delta + targets.length) % targets.length;
    });
  }

  /* --- content, every piece of it optional ----------------------------- */

  const title = card.title?.trim() ?? '';
  const reference = showReference ? (card.reference?.trim() ?? '') : '';
  const company = card.company?.name.trim() ?? '';
  const contact = card.contact?.name.trim() ?? '';
  /*
   * COMPANIES BOARD ONLY (owner 2026-10-23): the company's Stage status, shown
   * under the title — the column already names the stage. Read through
   * `CompaniesBoardCard` because the key is absent on every other board (and
   * on a companies board served before the backend sends it), so absent, null
   * and a blank name all render nothing. Visually just the status; the
   * screen-reader prefix names the stage when the board passes `columnName`.
   */
  const rawStatusName = (card as CompaniesBoardCard).stageStatus?.name;
  const stageStatus = typeof rawStatusName === 'string' ? rawStatusName.trim() : '';
  const stageName = columnName?.trim() ?? '';
  const stageStatusPrefix = stageName !== '' ? `${stageName} status: ` : 'Stage status: ';

  // `amount` is an exact DECIMAL string. Never `Number()` it, and never let a
  // missing amount render as 0.00 — unpriced and zero are different facts.
  // On the deals board it is already the deal VALUE (lines total, else typed
  // amount), resolved server-side — do not re-derive it here.
  const amount = card.amount?.trim() ?? '';
  const currency = card.currency?.trim().toUpperCase() ?? '';
  const hasAmount = amount !== '';

  /*
   * TWO independent absences, both answered here with the same nothing:
   *
   * - `dateField === null` — this BOARD has no date at all (companies). There
   *   is no field to name, so there is no label to speak either;
   * - `card.date === null` — this board has a date and this record has not
   *   been given one.
   *
   * `dateLabel` is derived alongside the tone so `KANBAN_DATE_LABEL` is
   * indexed exactly once, here, where the null is still in hand — never at the
   * three call sites below, where a null field would have read out as
   * "undefined: " in the `sr-only` label and the `title`.
   */
  const dateLabel = dateField === null ? null : KANBAN_DATE_LABEL[dateField];
  const tone =
    dateField !== null && card.date !== null ? (dateTone(card.date, dateField) ?? 'normal') : null;
  const date = tone === null ? null : DATE_TONE[tone];

  const owner = card.owner;
  // `name` is '' on records the API could not resolve a display name for, and
  // `email` is nullable, so the id is the last honest fallback.
  const ownerLabel = owner ? owner.name.trim() || owner.email?.trim() || `User #${owner.id}` : '';
  const ownerHasName = owner !== null && owner.name.trim() !== '';

  /** Names the record in labels that are read out of context (button, menu). */
  const cardLabel = title !== '' ? title : `#${card.id}`;

  /*
   * How much of the title's first line the corner controls have taken.
   *
   * Both controls are absolutely positioned, so neither costs the card any
   * height and neither pushes the title anywhere — this padding is the ONLY
   * thing either one takes from the layout, and it is taken only for the
   * controls that actually rendered. With no controls it is `null` and `cx`
   * drops it, so the class string on a card with neither is byte-for-byte the
   * one it has always had.
   *
   * The figures are the control widths, not guesses: the `sm` icon-only button
   * is 32px and bumps to a 40px touch target below `sm`, so one control needs
   * ~36px of the line and two need ~72px, measured from the card's border
   * rather than from the title's own box. Rounded up to the scale above each.
   */
  const controlCount =
    (canMove ? 1 : 0) + (cardAction !== undefined ? Math.max(1, cardActionCount) : 0);
  const titleClearance =
    controlCount === 0
      ? null
      : controlCount === 1
        ? 'pr-8 max-sm:pr-10'
        : controlCount === 2
          ? 'pr-16 max-sm:pr-20'
          : 'pr-24 max-sm:pr-32';
  /*
   * Quote signed (owner 2026-10-27), DEALS BOARD ONLY. Absent on every other
   * board and on an older API; `null` = no linked quote is signed. Both render
   * nothing — only a signed deal gets the badge.
   */
  const quoteSigned = card.quoteSigned ?? null;
  const hasFacts = hasAmount || date !== null || reference !== '' || quoteSigned !== null;
  const hasPeople = contact !== '' || owner !== null;
  /* The rule exists to separate two things. With one of them missing there is
   * nothing to separate, so it is not drawn — an empty card gets no furniture. */
  const hasSupport = hasFacts || hasPeople;

  return (
    <article
      aria-busy={moving || undefined}
      className={cx(
        'group relative rounded-lg border border-border bg-surface px-3.5 py-3',
        /*
         * The Move control is absolutely positioned, so it adds no height, and
         * a title-only card can still come out shorter than the button floating
         * in it. The floor clears the button and nothing more — it is not where
         * the card gets its size from; the type scale and the gaps are.
         */
        'min-h-12 max-sm:min-h-14',
        'transition-[border-color,background-color,opacity] duration-150 ease-out motion-reduce:transition-none',
        'hover:border-hairline-strong hover:bg-surface-hover',
        'focus-within:border-hairline-strong',
        /*
         * 🔴 The red left rail for an overdue card is REMOVED — UI rework,
         * finding 18 (2026-10-11): overdue was signalled three ways (rail,
         * badge, red text). *SUPERSEDED: `tone === 'overdue' && 'border-l-2
         * border-l-danger'`.* The badge below is now the one signal, and it
         * carries the word AND the glyph, so colour is still never alone.
         */
        moving && 'opacity-60',
      )}
    >
      {/*
       * Stretched link. Painted over the content so the whole card is one
       * target, with `draggable={false}` so a drag begun on it becomes the
       * board's card drag rather than the browser's native link drag.
       */}
      <Link
        ref={linkRef}
        to={href}
        draggable={false}
        aria-labelledby={titleId}
        className="absolute inset-0 rounded-lg"
      />

      {/*
       * `gap-*` and not `space-y`: the gap only exists between blocks that
       * actually rendered, so a title-only card carries no trailing rhythm
       * from fields it does not have.
       */}
      <div className="flex min-w-0 flex-col gap-3">
        {/* Tier 1 — what this record is. Tighter internally than the gap that
            follows it, so the title and its company read as one block. */}
        <div className="flex min-w-0 flex-col gap-1.5">
          <h3
            id={titleId}
            className={cx(
              // The one dominant thing on the card, and the reason the card got
              // bigger at all: a full step of size, weight and colour above
              // everything under it.
              'text-lg font-semibold break-words text-fg',
              // Three lines then ellipsis: the wider column fits the long
              // "Institution - Engagement" titles in two, and the third is
              // headroom rather than licence for a title to eat the column.
              'line-clamp-3',
              // Clearance for the icon-only controls in the corner; they are
              // wider on a phone, where the primitives bump to a 40px target.
              titleClearance,
            )}
          >
            {title !== '' ? (
              title
            ) : (
              // Lighter than a real title, so a column of untitled records reads
              // as records without a name rather than as the loudest cards there.
              <span className="font-medium text-fg-muted">
                <span aria-hidden="true">Untitled </span>
                <span className="sr-only">Untitled record </span>
                <span className="tabular text-fg-subtle">#{card.id}</span>
              </span>
            )}
          </h3>

          {/*
           * THE SCHOOL NAME. It is the account the whole record hangs off, so
           * it takes the position directly under the title, at a size nothing
           * else on the card shares. It wraps rather than truncating — an
           * institution clipped to "University of the Western C…" identifies
           * nothing — and two lines is the ceiling.
           *
           * Null on every record in production today. Nothing reserves space
           * for it, so a card with no company reads as complete rather than as
           * one with a blank where a name goes.
           */}
          {/*
           * No clearance for the Move control here, unlike the title: the
           * control is pinned to the first line and the title always occupies
           * it, so this line is already below the button. Reserving 32px on a
           * 280px phone column would cost the school name a wrap for nothing.
           */}
          {company !== '' && (
            <p className="m-0 flex min-w-0 items-start gap-1.5 text-sm font-medium text-fg-secondary">
              <CompanyIcon />
              <span className="sr-only">Company: </span>
              <span className="line-clamp-2 min-w-0 break-words">{company}</span>
            </p>
          )}
          {stageStatus !== '' && (
            <p className="m-0 min-w-0 text-sm text-fg-secondary">
              <span className="sr-only">{stageStatusPrefix}</span>
              <span className="line-clamp-2 min-w-0 break-words">{stageStatus}</span>
            </p>
          )}
        </div>

        {/*
         * Tier 2 and 3, behind one hairline. The rule is what buys the title
         * its quiet — everything below it is supporting detail and now says so
         * structurally, instead of relying on four descending font sizes to
         * carry the whole hierarchy.
         */}
        {hasSupport && (
          <div className="flex min-w-0 flex-col gap-2 border-t border-border pt-2.5">
            {hasFacts && (
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                {/*
                 * The date leads the row: it is what this board is scanned for,
                 * and on the two boards that carry no money it is the only fact
                 * in the row at all.
                 *
                 * The field name is spoken but not painted: the board shows one
                 * date field for every card in every column, so "Closing date"
                 * on every card is noise a reader has to look past to reach the
                 * date itself. The icon says which kind of date it is, the
                 * `sr-only` label says it in words, and the `title` is a
                 * best-effort tooltip — the stretched link sits over this text,
                 * so it rarely fires.
                 *
                 * The urgent states keep their badge, and the badge keeps its
                 * word AND its glyph: colour is never the only carrier. The
                 * inner spans set their own font size, so the text steps up to
                 * 12px inside a primitive that is 11px by default without
                 * fighting it over class order.
                 */}
                {date !== null &&
                  dateLabel !== null &&
                  (date.badge !== null ? (
                    <Badge tone={date.badge} icon={<date.Icon />}>
                      <span className="sr-only">{dateLabel}: </span>
                      <span className="text-xs font-semibold">{date.word}</span>
                      <span aria-hidden="true" className="text-xs">
                        {' · '}
                      </span>
                      <span className="tabular text-xs font-normal">
                        {formatDateOnly(card.date)}
                      </span>
                    </Badge>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 font-medium text-fg-secondary"
                      title={dateLabel}
                    >
                      <date.Icon />
                      <span className="sr-only">{dateLabel}: </span>
                      <span className="tabular">{formatDateOnly(card.date)}</span>
                    </span>
                  ))}

                {hasAmount && (
                  <Money
                    amount={amount}
                    currency={currency !== '' ? currency : null}
                    className="text-sm font-semibold text-fg"
                  />
                )}

                {reference !== '' && (
                  <span className="tabular text-2xs text-fg-subtle">{reference}</span>
                )}

                {/* Under the stretched link, so it is a mark, not a link; the
                    title attribute carries the date for pointer readers. */}
                {quoteSigned !== null && (
                  <span title={quoteSignedLabel(quoteSigned)}>
                    <Badge tone="success">{QUOTE_SIGNED_LABEL}</Badge>
                  </span>
                )}
              </div>
            )}

            {/*
             * Contact and owner share one wrapping line. Both are null on
             * essentially every production record, so the populated card pays
             * for them and the empty one shows nothing at all.
             */}
            {hasPeople && (
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-fg-muted">
                {contact !== '' && <span className="truncate">{contact}</span>}
                {/* A separator only when there is something on both sides of it. */}
                {contact !== '' && owner && (
                  <span aria-hidden="true" className="text-fg-subtle">
                    ·
                  </span>
                )}
                {owner && (
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    {ownerHasName ? <Avatar name={ownerLabel} size="sm" /> : <PersonIcon />}
                    <span className="truncate">{ownerLabel}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/*
       * THE PAGE'S OWN PER-CARD ACTION — the same "create a document from this
       * record" menu the table rows carry, handed in by the page.
       *
       * Above the stretched link for the same reason the Move control is, and
       * given the corner slot to the LEFT of it, so neither block has to read
       * the other's classes and the Move control is untouched by this feature.
       * Absolutely positioned, so it costs no height and no width: it cannot
       * push the title anywhere, and a card whose page passes nothing renders
       * neither this block nor a slot where it would have gone.
       *
       * VISIBLE AT ALL TIMES, unlike the Move control, which is revealed by
       * hover or focus. That reveal leans on `group-focus-within`, which is a
       * DOM relationship — and this action is a page's component, very likely a
       * menu portalled to the body. Focus inside a portal is NOT inside this
       * card's subtree, so the trigger would fade out from under a menu that is
       * still open. Being quietly present beats vanishing while in use.
       */}
      {cardAction && (
        <div
          /*
           * ⚠️ `draggable={false}` ALONE DOES NOT TAKE THIS OUT OF THE DRAG.
           * The board sets `draggable`/`onDragStart` on the WRAPPING `<li>`,
           * and per the HTML drag spec the drag source is the nearest
           * draggable ancestor — a non-draggable child is still dragged as
           * part of it, and `dragstart` fires at the `<li>`, never reaching a
           * guard here. Press-and-twitch on this trigger would move the CARD,
           * and the drag then suppresses the click that would have opened the
           * menu — nothing errors, nothing looks wrong, until someone uses it.
           *
           * `data-kanban-no-drag` is the real fix: the board watches for a
           * pointerdown inside it and lifts `draggable` off the `<li>` for
           * that press. The two attributes below are the second and third
           * lines of defence.
           */
          data-kanban-no-drag=""
          draggable={false}
          onDragStart={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          /*
           * The whole card is a stretched `<Link>`. This block sits above it,
           * so the link never receives the click — but the action may portal a
           * menu, and React propagates a portal's events through the REACT
           * tree regardless of where the DOM node lives, so a menu item's click
           * arrives here anyway. Stopped, so it can never reach a handler the
           * card or the column may gain later.
           *
           * `pointerdown` is deliberately NOT stopped: the Move menu closes on
           * a document-level pointerdown listener, and swallowing it here would
           * leave that menu hanging open underneath this one.
           */
          onClick={(event) => event.stopPropagation()}
          className={cx(
            'absolute top-1 z-10',
            // One control's width plus a hairline gap when the Move control is
            // there, its place when it is not. Both figures track the button
            // sizes the title clearance above is cut from.
            canMove ? 'right-10 max-sm:right-12' : 'right-1',
          )}
        >
          {cardAction(card)}
        </div>
      )}

      {/*
       * Above the stretched link, and rendered after it so it wins the stacking
       * order without either one needing a z-index war. A viewer gets no
       * control at all rather than a button that 403s.
       */}
      {canMove && (
        <div
          draggable={false}
          className={cx(
            // Sits on the title's first line rather than in the very corner:
            // with the roomier padding, a corner-pinned control floats in
            // whitespace instead of reading as part of the card's top line.
            'absolute top-1 right-1 z-10',
            /*
             * Quiet until it is wanted. Revealed by hovering or focusing
             * anywhere on the card, and permanently visible at touch sizes and
             * on a coarse pointer, where there is no hover to reveal it and
             * this menu is the only way to move a card at all.
             *
             * Opacity, never `hidden` or conditional rendering: the button
             * stays in the tab order at all times, so the keyboard path does
             * not silently disappear along with the pixels. It reveals itself
             * on focus through the same `group-focus-within`.
             */
            menuOpen || moving
              ? 'opacity-100'
              : cx(
                  'opacity-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
                  'group-hover:opacity-100 group-focus-within:opacity-100',
                  'max-sm:opacity-100 pointer-coarse:opacity-100',
                ),
          )}
        >
          <Button
            ref={triggerRef}
            size="sm"
            variant="ghost"
            iconOnly
            disabled={menuDisabled}
            loading={moving}
            leadingIcon={<MoveIcon />}
            onClick={onTriggerClick}
            onKeyDown={onTriggerKeyDown}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            /*
             * Icon-only, because a labelled button in the corner cost the title
             * five characters of width on every card in the board. The name of
             * the record goes in the label so the control is unambiguous out of
             * context, where a screen reader meets 76 identical "Move" buttons.
             */
            aria-label={moving ? `Moving ${cardLabel}…` : `Move ${cardLabel} to another column`}
            title={
              hasTargets
                ? 'Move to another column'
                : 'Nowhere to move this to — this board has no other active columns'
            }
          />
        </div>
      )}

      {/* Announced politely, so the pending state is not opacity alone. */}
      <span role="status" className="sr-only">
        {moving ? 'Moving card…' : ''}
      </span>

      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={`Move ${cardLabel} to`}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              right: rect.right,
              maxHeight: MENU_MAX_HEIGHT,
              ...(rect.flip
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
            }}
            className="z-[60] max-w-[min(18rem,calc(100vw-1rem))] min-w-52 overflow-y-auto overscroll-contain rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
          >
            <p className="px-2 py-1 text-xs font-semibold text-fg-subtle">
              Move to
            </p>
            {targets.map((target, index) => (
              <button
                key={target.id}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                type="button"
                role="menuitem"
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  commit(target.id);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cx(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm max-sm:min-h-11',
                  'text-fg-secondary hover:bg-surface-hover hover:text-fg',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{target.name}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </article>
  );
}
